/**
 * Casarto Model Selector
 * STABILE VERSION - Behebt den Flickity/Slider Crash beim Neuladen
 */

class ModelSelector {
  constructor() {
    this.data = null;
    this.selectedModel = null;
    this.selectedBrand = null;
    this.hasUserInteracted = false;
    this.imageCache = new Map();
    this.pendingTransitions = new Set();
    // Notfall-Modell: greift, wenn ein Produkt weder ein Bild fuer das
    // gewaehlte Modell noch irgendein generisches (nicht modellgebundenes)
    // Bild hat - besser ein plausibles Vorschaubild als eine leere Galerie.
    this.fallbackModel = 'iPhone 17 Pro';

    this.wrapper = document.querySelector('[data-model-selector]');
    if (this.wrapper) {
      this.trigger = this.wrapper.querySelector('[data-model-selector-trigger]');
      this.dropdown = this.wrapper.querySelector('[data-model-dropdown]');
      this.searchInput = this.wrapper.querySelector('[data-model-search]');
      this.modelList = this.wrapper.querySelector('[data-model-list]');
      this.clearButton = this.wrapper.querySelector('[data-model-clear]');
      this.label = this.wrapper.querySelector('[data-model-label]');
      
      this.mobileTrigger = this.wrapper.querySelector('[data-model-mobile-trigger]');
      this.modal = this.wrapper.querySelector('[data-model-modal]');
      this.modalOverlay = this.wrapper.querySelector('[data-modal-overlay]');
      this.modalClose = this.wrapper.querySelector('[data-modal-close]');
      this.searchInputMobile = this.wrapper.querySelector('[data-model-search-mobile]');
      this.modelListMobile = this.wrapper.querySelector('[data-model-list-mobile]');
      this.clearButtonMobile = this.wrapper.querySelector('[data-model-clear-mobile]');
    }
    
    this.drawerTrigger = document.querySelector('[data-model-selector-drawer-trigger]');
    this.drawerDropdown = document.querySelector('[data-model-dropdown-drawer]');
    this.drawerSearchInput = document.querySelector('[data-model-search-drawer]');
    this.drawerModelList = document.querySelector('[data-model-list-drawer]');
    this.drawerClearButton = document.querySelector('[data-model-clear-drawer]');
    this.drawerLabel = document.querySelector('[data-model-label-drawer]');
    
    this.sectionTrigger = document.querySelector('[data-model-selector-section-trigger]');
    this.sectionDropdown = document.querySelector('[data-model-dropdown-section]');
    this.sectionSearchInput = document.querySelector('[data-model-search-section]');
    this.sectionModelList = document.querySelector('[data-model-list-section]');
    this.sectionClearButton = document.querySelector('[data-model-clear-section]');
    this.sectionLabel = document.querySelector('[data-model-label-section]');
    
    this.init();
  }

  getSelectionLabel() { return [this.selectedBrand, this.selectedModel].filter(Boolean).join(' '); }
  isMobileViewport() { return window.matchMedia ? window.matchMedia('(max-width: 749px)').matches : window.innerWidth < 750; }
  
  init() {
    this.loadData();
    this.buildModelSlugSet();
    this.loadSavedModel();
    this.attachEventListeners();
    this.updateUI();
    
    // Wir führen den Filter sofort aus. Dadurch werden falsche Bilder versteckt, 
    // noch BEVOR das Theme den Slider baut.
    this.applyModelToPage();
    
    // Für Theme-interne Navigation (AJAX/Turbo)
    document.addEventListener('page:loaded', () => {
      this.applyModelToPage();
    });
  }
  
  loadData() {
    const dataScript = document.querySelector('[data-model-selector-data]');
    if (dataScript) {
      try { this.data = JSON.parse(dataScript.textContent); } 
      catch (e) { console.error('Model Selector: Failed to parse data', e); }
    }
  }

  buildModelSlugSet() {
    this.modelSlugSet = new Set();
    if (!this.data || !this.data.models) return;
    Object.values(this.data.models).forEach(models => {
      models.forEach(m => this.modelSlugSet.add(this.normalizeModelName(m).toLowerCase()));
    });
  }

  // Zerlegt Dateiname/Alt-Text in Tokens. Trennzeichen sind nur "_" und ".",
  // damit mehrteilige Modell-Slugs wie "iphone-17-pro" (mit Bindestrich) als
  // EIN Token erhalten bleiben - das verhindert Fehltreffer wie "iPhone 17 Pro"
  // vs. "iPhone 17 Pro Max". Ein Token kann am Anfang, in der Mitte oder direkt
  // vor der Dateiendung stehen (kein Underscore am Ende mehr nötig).
  tokenizeIdentifierText(text) {
    return (text || '').toLowerCase().split(/[_.]+/).filter(Boolean);
  }

  isKnownModelIdentifierFromText(text) {
    if (!text || !this.modelSlugSet) return false;
    return this.tokenizeIdentifierText(text).some(token => this.modelSlugSet.has(token));
  }

  textMatchesModel(text, modelSlugLower) {
    if (!text || !modelSlugLower) return false;
    return this.tokenizeIdentifierText(text).includes(modelSlugLower);
  }
  
  loadSavedModel() {
    const saved = this.getCookie(this.data?.cookieName);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        this.selectedBrand = parsed.brand;
        this.selectedModel = parsed.model;
        this.hasUserInteracted = true;
      } catch (e) {}
    } else {
      this.selectedBrand = 'Apple';
      this.selectedModel = 'iPhone 17 Pro';
      this.hasUserInteracted = false;
    }
  }
  
  attachEventListeners() {
    if (this.trigger) this.trigger.addEventListener('click', () => this.toggleDropdown());
    if (this.searchInput) this.searchInput.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.modelList), 200));
    if (this.clearButton) this.clearButton.addEventListener('click', () => this.clearSelection());
    if (this.mobileTrigger) this.mobileTrigger.addEventListener('click', () => this.openModal());
    if (this.modalOverlay) this.modalOverlay.addEventListener('click', () => this.closeModal());
    if (this.modalClose) this.modalClose.addEventListener('click', () => this.closeModal());
    if (this.searchInputMobile) this.searchInputMobile.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.modelListMobile), 200));
    if (this.clearButtonMobile) this.clearButtonMobile.addEventListener('click', () => { this.clearSelection(); this.closeModal(); });
    
    if (this.drawerTrigger) this.drawerTrigger.addEventListener('click', () => this.toggleDrawerDropdown());
    if (this.drawerSearchInput) this.drawerSearchInput.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.drawerModelList), 200));
    if (this.drawerClearButton) this.drawerClearButton.addEventListener('click', () => { this.clearSelection(); this.closeDrawerDropdown(); });
    
    if (this.sectionTrigger) this.sectionTrigger.addEventListener('click', () => this.toggleSectionDropdown());
    if (this.sectionSearchInput) this.sectionSearchInput.addEventListener('input', this.debounce((e) => this.filterModels(e.target.value, this.sectionModelList), 200));
    if (this.sectionClearButton) this.sectionClearButton.addEventListener('click', () => { this.clearSelection(); this.closeSectionDropdown(); });
    
    document.addEventListener('click', (e) => {
      if (this.wrapper && !this.wrapper.contains(e.target)) this.closeDropdown();
      const drawerSelector = document.querySelector('.model-selector--drawer');
      if (drawerSelector && !drawerSelector.contains(e.target)) this.closeDrawerDropdown();
      const sectionSelector = document.querySelector('.model-selector-section__search-container');
      if (sectionSelector && !sectionSelector.contains(e.target)) this.closeSectionDropdown();
    });
    
    document.addEventListener('variant:change', (e) => {
      if (e.detail && e.detail.variant) this.syncWithVariantPicker(e.detail.variant);
    });
  }
  
  toggleDropdown() { this.dropdown.hidden ? this.openDropdown() : this.closeDropdown(); }
  openDropdown() { this.renderModelList(this.modelList); this.dropdown.hidden = false; this.searchInput && this.searchInput.focus(); }
  closeDropdown() { if (!this.dropdown) return; this.dropdown.hidden = true; if (this.searchInput) this.searchInput.value = ''; }
  openModal() { this.renderModelList(this.modelListMobile); if (!this.modal) return; this.modal.hidden = false; document.body.style.overflow = 'hidden'; setTimeout(() => { this.searchInputMobile && this.searchInputMobile.focus(); }, 300); }
  closeModal() { if (!this.modal) return; this.modal.hidden = true; document.body.style.overflow = ''; if (this.searchInputMobile) this.searchInputMobile.value = ''; }
  toggleDrawerDropdown() { if (!this.drawerDropdown) return; this.drawerDropdown.hidden ? this.openDrawerDropdown() : this.closeDrawerDropdown(); }
  openDrawerDropdown() { this.renderModelList(this.drawerModelList); if (!this.drawerDropdown) return; this.drawerDropdown.hidden = false; this.drawerSearchInput && this.drawerSearchInput.focus(); }
  closeDrawerDropdown() { if (!this.drawerDropdown) return; this.drawerDropdown.hidden = true; if (this.drawerSearchInput) this.drawerSearchInput.value = ''; }
  toggleSectionDropdown() { if (!this.sectionDropdown) return; this.sectionDropdown.hidden ? this.openSectionDropdown() : this.closeSectionDropdown(); }
  openSectionDropdown() { this.renderModelList(this.sectionModelList); if (!this.sectionDropdown) return; this.sectionDropdown.hidden = false; this.sectionSearchInput && this.sectionSearchInput.focus(); }
  closeSectionDropdown() { if (!this.sectionDropdown) return; this.sectionDropdown.hidden = true; if (this.sectionSearchInput) this.sectionSearchInput.value = ''; }
  
  renderModelList(container, filter = '') {
    if (!container || !this.data) return;
    const filterLower = filter.toLowerCase();
    let html = '';
    
    Object.entries(this.data.models).forEach(([brand, models]) => {
      const filteredModels = filter ? models.filter(m => m.toLowerCase().includes(filterLower)) : models;
      if (filteredModels.length === 0) return;
      html += `<div class="model-selector__group"><div class="model-selector__group-title">${brand}</div><div class="model-selector__group-items">${filteredModels.map(model => `<button type="button" class="model-selector__item${this.selectedModel === model ? ' model-selector__item--active' : ''}" data-brand="${brand}" data-model="${model}">${model}</button>`).join('')}</div></div>`;
    });
    
    if (html === '') html = '<div class="model-selector__no-results">Keine Modelle gefunden</div>';
    container.innerHTML = html;
    
    container.querySelectorAll('[data-model]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.selectModel(e.currentTarget.dataset.brand, e.currentTarget.dataset.model);
      });
    });
  }
  
  filterModels(query, container) { this.renderModelList(container, query); }
  
  selectModel(brand, model) {
    this.selectedBrand = brand; this.selectedModel = model; this.hasUserInteracted = true;
    this.setCookie(this.data.cookieName, JSON.stringify({ brand, model }), this.data.cookieExpiry);
    this.updateUI(); this.closeDropdown(); this.closeModal(); this.closeDrawerDropdown(); this.closeSectionDropdown();
    this.applyModelToPage();
    document.dispatchEvent(new CustomEvent('model:selected', { detail: { brand, model } }));
  }
  
  clearSelection() {
    this.selectedBrand = 'Apple'; this.selectedModel = 'iPhone 17 Pro'; this.hasUserInteracted = false;
    this.deleteCookie(this.data.cookieName); this.updateUI(); this.applyModelToPage();
    document.dispatchEvent(new CustomEvent('model:cleared'));
  }
  
  updateUI() {
    if (this.label) this.label.textContent = this.hasUserInteracted ? this.getSelectionLabel() : 'z.B. iPhone 17, Galaxy S25 ...';
    if (this.mobileTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.mobileTrigger.classList.add('model-selector__mobile-trigger--active');
        this.mobileTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.mobileTrigger.classList.remove('model-selector__mobile-trigger--active');
        this.mobileTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
    if (this.drawerLabel) this.drawerLabel.textContent = this.hasUserInteracted ? this.getSelectionLabel() : 'z.B. iPhone 17, Galaxy S25 ...';
    if (this.drawerTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.drawerTrigger.classList.add('model-selector__trigger--has-selection');
        this.drawerTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.drawerTrigger.classList.remove('model-selector__trigger--has-selection');
        this.drawerTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
    if (this.sectionLabel && this.hasUserInteracted) this.sectionLabel.textContent = this.getSelectionLabel();
    if (this.sectionTrigger) {
      if (this.hasUserInteracted && this.selectedModel) {
        this.sectionTrigger.classList.add('model-selector-section__trigger--has-selection');
        this.sectionTrigger.setAttribute('aria-label', `Ausgewählt: ${this.getSelectionLabel()}`);
      } else {
        this.sectionTrigger.classList.remove('model-selector-section__trigger--has-selection');
        this.sectionTrigger.setAttribute('aria-label', 'Handymodell auswählen');
      }
    }
  }
  
  applyModelToPage() {
    const modelSlug = this.normalizeModelName(this.selectedModel);
    const imageSlug = this.normalizeModelName(this.resolveImageModel(this.selectedModel));
    this.updateVariantPicker();
    this.filterProductImages(imageSlug);
    this.updateCollectionGrid(imageSlug);
  }

  // Löst ein Modell über die imageGroups-Tabelle auf ein anderes Modell auf,
  // dessen Bilder stattdessen verwendet werden sollen (z.B. S25 Plus -> S25).
  resolveImageModel(model) {
    if (!model || !this.data || !this.data.imageGroups) return model;
    return this.data.imageGroups[model] || model;
  }
  
  filterProductImages(modelSlug) {
    const modelSlugLower = (modelSlug || '').toLowerCase();

    const slideshow = document.querySelector('.product-slideshow');
    const allSlides = document.querySelectorAll('.product-main-slide');
    const thumbs = document.querySelectorAll('[data-product-thumbs] .product__thumb-item');

    if (!slideshow || allSlides.length === 0) return;

    // 1. Bilder markieren (ein-/ausblenden) fuer das gewaehlte Modell.
    let effectiveSlugLower = modelSlugLower;
    let visibility = this.applyModelVisibility(allSlides, thumbs, effectiveSlugLower);

    // 1a. Fallback: Entweder ist die Galerie komplett leer, ODER das Produkt
    // unterscheidet grundsaetzlich nach Modellen (es gibt modellgetaggte
    // Bilder), aber keines davon passt zum gewaehlten Modell - dann wuerden
    // nur generische Deko-Bilder (z.B. "Catwalk"-Illustrationen ohne
    // Modell-Tag) angezeigt, die nichts mit dem Handymodell zu tun haben.
    // In beiden Faellen weichen wir auf ein festes Ersatzmodell aus, dessen
    // Bilder praktisch immer vorhanden sind.
    const needsFallback = !visibility.hasVisible ||
      (!visibility.hasModelMatch && visibility.hasAnyKnownModelIdentifier);

    if (needsFallback) {
      const fallbackSlugLower = this.normalizeModelName(this.fallbackModel).toLowerCase();
      const fallbackVisibility = this.applyModelVisibility(allSlides, thumbs, fallbackSlugLower);
      if (fallbackVisibility.hasModelMatch) {
        effectiveSlugLower = fallbackSlugLower;
        visibility = fallbackVisibility;
      } else {
        // Das Ersatzmodell hat selbst keine eigenen Bilder - zurueck zur
        // urspruenglichen Auswahl (zeigt zumindest generische Bilder, falls vorhanden).
        visibility = this.applyModelVisibility(allSlides, thumbs, effectiveSlugLower);
      }
    }

    const hasVisibleSlides = visibility.hasVisible;

    // 1b. Sowohl die Hauptbild-Slides als auch die Thumbnails PHYSISCH so
    // umsortieren, dass das Bild des aktuellen (bzw. Fallback-)Modells
    // garantiert als erstes sichtbares Element erscheint - unabhaengig davon,
    // wo es urspruenglich in der Upload-Reihenfolge stand oder ob Flickity
    // die Slide-Reihenfolge durch vorherige destroy/rebuild-Zyklen bereits
    // verschoben hat.
    this.reorderSlidesForModel(allSlides, effectiveSlugLower);
    this.reorderThumbsForModel(effectiveSlugLower);

    // 2. Slider synchronisieren.
    if (hasVisibleSlides) {
      const isFlickityActive = slideshow.classList.contains('flickity-enabled');

      if (isFlickityActive) {
        // Slider war schon da (z.B. durch Klick auf Modell). Wir bauen ihn sauber neu.
        this.rebuildThemeSlider(allSlides, slideshow);
      } else {
        // Auf F5 existiert der Slider noch nicht (flickity-enabled fehlt).
        // Das Theme baut ihn selbst - ABER es berechnet den Startindex aus dem
        // rohen data-index der .starting-slide (Position unter ALLEN Bildern),
        // was nicht zur gefilterten (":not(.hide)") Zellreihenfolge von Flickity
        // passt. Ergebnis: Hauptbild und aktives Thumbnail zeigen dann
        // unterschiedliche Motive. Wir warten deshalb, bis das Theme fertig
        // gebaut hat, und korrigieren danach einmalig mit derselben Logik wie
        // beim Klick-Fall.
        this.waitForSliderThenRebuild(slideshow);
      }
    }
  }

  // Blendet Slides/Thumbnails fuer das angegebene Modell ein bzw. aus:
  // Bilder mit erkanntem Modell-Tag, der NICHT zum gewaehlten Modell passt,
  // werden versteckt; generische Bilder ohne Modell-Tag bleiben immer
  // sichtbar. Gibt zurueck: hasVisible (mind. ein Slide sichtbar),
  // hasModelMatch (mind. ein Bild TATSAECHLICH mit dem Modell getaggt) und
  // hasAnyKnownModelIdentifier (das Produkt hat ueberhaupt modellgetaggte
  // Bilder, unterscheidet also grundsaetzlich nach Modell).
  applyModelVisibility(allSlides, thumbs, slugLower) {
    let hasVisible = false;
    let hasModelMatch = false;
    let hasAnyKnownModelIdentifier = false;

    allSlides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;
      const src = this.extractFileKey(img.src || img.dataset.src || '');
      const alt = (img.alt || '').toLowerCase();
      const containsModel = this.textMatchesModel(src, slugLower) || this.textMatchesModel(alt, slugLower);
      const hasKnownModelIdentifier = this.isKnownModelIdentifierFromText(src) || this.isKnownModelIdentifierFromText(alt);

      if (hasKnownModelIdentifier) hasAnyKnownModelIdentifier = true;
      if (containsModel) hasModelMatch = true;

      if (hasKnownModelIdentifier && !containsModel) {
        slide.classList.add('hide');
      } else {
        slide.classList.remove('hide');
        hasVisible = true;
      }
    });

    thumbs.forEach((thumb) => {
      const img = thumb.querySelector('img');
      if (!img) return;
      const src = this.extractFileKey(img.src || img.dataset.src || '');
      const alt = (img.alt || '').toLowerCase();
      const containsModel = this.textMatchesModel(src, slugLower) || this.textMatchesModel(alt, slugLower);
      const hasKnownModelIdentifier = this.isKnownModelIdentifierFromText(src) || this.isKnownModelIdentifierFromText(alt);

      if (hasKnownModelIdentifier && !containsModel) {
        thumb.classList.add('hide');
      } else {
        thumb.classList.remove('hide');
      }
    });

    return { hasVisible, hasModelMatch, hasAnyKnownModelIdentifier };
  }

  // Wartet darauf, dass das Theme den Flickity-Slider fertig initialisiert hat
  // (Klasse "flickity-enabled" erscheint), und erzwingt danach einmalig einen
  // korrigierten Rebuild - siehe Kommentar in filterProductImages().
  waitForSliderThenRebuild(slideshowContainer) {
    if (this._sliderWatcher) return;
    let attempts = 0;
    this._sliderWatcher = setInterval(() => {
      attempts++;
      if (slideshowContainer.classList.contains('flickity-enabled')) {
        clearInterval(this._sliderWatcher);
        this._sliderWatcher = null;
        const currentSlides = document.querySelectorAll('.product-main-slide');
        this.rebuildThemeSlider(currentSlides, slideshowContainer);
      } else if (attempts > 40) { // Sicherheits-Timeout nach ~2s
        clearInterval(this._sliderWatcher);
        this._sliderWatcher = null;
      }
    }, 50);
  }

  // Bestimmt die Sortier-Prioritaet eines Slide-/Thumbnail-Elements fuer das
  // aktuell gewaehlte Modell: 0 = zeigt genau dieses Modell, 1 = generisches
  // Bild ohne Modellbezug (immer sichtbar), 2 = zeigt ein anderes Modell
  // (ausgeblendet).
  computeModelPriority(item, modelSlugLower) {
    const img = item.querySelector('img');
    if (!img) return 2;
    const src = this.extractFileKey(img.src || img.dataset.src || '');
    const alt = (img.alt || '').toLowerCase();
    const containsModel = this.textMatchesModel(src, modelSlugLower) || this.textMatchesModel(alt, modelSlugLower);
    const hasKnownModelIdentifier = this.isKnownModelIdentifierFromText(src) || this.isKnownModelIdentifierFromText(alt);
    if (hasKnownModelIdentifier && containsModel) return 0;
    if (!hasKnownModelIdentifier) return 1;
    return 2;
  }

  // Sortiert die uebergebenen Elemente physisch innerhalb ihres gemeinsamen
  // Containers neu: zum aktuellen Modell passende zuerst (in ihrer bisherigen
  // Reihenfolge untereinander), danach generische/immer sichtbare Bilder,
  // danach die (ausgeblendeten) anderer Modelle. Da appendChild bestehende
  // Knoten nur VERSCHIEBT (nicht kloniert), bleiben alle Event-Listener und
  // der spaeter von Flickity vergebene data-index (der die neue physische
  // Reihenfolge uebernimmt) korrekt erhalten.
  reorderItemsForModel(container, items, modelSlugLower) {
    if (!container || items.length === 0) return;

    const sorted = items
      .map((item, i) => ({ item, i, p: this.computeModelPriority(item, modelSlugLower) }))
      .sort((a, b) => a.p - b.p || a.i - b.i);

    // Nur tatsaechlich neu anhaengen, wenn sich die Reihenfolge auch aendert -
    // vermeidet unnoetige DOM-Writes/Reflows bei jedem Aufruf.
    const alreadyInOrder = sorted.every(({ item }, idx) => container.children[idx] === item);
    if (alreadyInOrder) return;

    sorted.forEach(({ item }) => container.appendChild(item));
  }

  reorderThumbsForModel(modelSlugLower) {
    const scroller = document.querySelector('[data-product-thumbs] .product__thumbs--scroller');
    if (!scroller) return;
    const items = Array.from(scroller.querySelectorAll(':scope > .product__thumb-item'));
    this.reorderItemsForModel(scroller, items, modelSlugLower);
  }

  // Sortiert auch die Hauptbild-Slides selbst um. Das ist der Grund, warum
  // beim allerersten Laden mit gespeichertem Modell (Cookie) bisher oft das
  // falsche Hauptbild zu sehen war: Flickity waehlt beim (Neu-)Aufbau immer
  // die erste sichtbare Zelle in DOM-Reihenfolge - stand dort urspruenglich
  // ein anderes (z.B. generisches) Bild vor dem Modellbild, wurde genau das
  // gezeigt, obwohl die Thumbnails (die separat umsortiert werden) schon
  // richtig aussahen. Jetzt landet das Modellbild deterministisch auch bei
  // den Slides an erster Stelle - unabhaengig vom Ladezeitpunkt oder von
  // vorherigem Flickity-internen DOM-Umherschieben durch wiederholte
  // destroy/rebuild-Zyklen.
  reorderSlidesForModel(allSlides, modelSlugLower) {
    const slidesArray = Array.from(allSlides);
    if (slidesArray.length === 0) return;
    const container = slidesArray[0].parentElement;
    if (!container) return;
    // Defensiv: nur Slides beruecksichtigen, die tatsaechlich direkte Kinder
    // desselben Containers sind (sollte im Normalfall auf alle zutreffen).
    const items = slidesArray.filter(slide => slide.parentElement === container);
    this.reorderItemsForModel(container, items, modelSlugLower);
  }

  // Funktion in eine eigene Methode ausgelagert
  rebuildThemeSlider(allSlides, slideshowContainer) {
    if (!(window.theme && window.theme.sections && window.theme.sections.instances)) return;
    const productInstance = window.theme.sections.instances.find(inst => inst.type === 'product');
    if (!productInstance || typeof productInstance.initProductSlider !== 'function') return;

    // Schnappschuss des aktuell sichtbaren Bildes fuer den Crossfade, BEVOR
    // irgendetwas am DOM/Slider veraendert wird.
    const currentImg = slideshowContainer.querySelector('.product-main-slide.is-selected img')
      || slideshowContainer.querySelector('.product-main-slide:not(.hide) img');
    const overlay = this.createSliderFadeOverlay(slideshowContainer, currentImg);

    // Höhe einfrieren
    const currentHeight = slideshowContainer.offsetHeight;
    if (currentHeight > 0) {
      slideshowContainer.style.minHeight = `${currentHeight}px`;
    }

    // Die eigene Hoehen-Transition des Themes (.flickity-viewport { transition: height }
    // in theme.css) erzeugt beim Rebuild sonst einen "Wipe von oben nach unten".
    // Waehrend das Overlay-Bild alles abdeckt, blenden wir diese Transition kurz
    // aus, damit der Hoehensprung dahinter unsichtbar/ohne Wipe passiert.
    slideshowContainer.classList.add('model-selector__no-wipe');

    // Der Slider wird mit cellSelector ':not(.hide)' gebaut (siehe theme.js),
    // Flickity zaehlt seine Zellen also nur unter den sichtbaren Slides neu durch.
    // Der erste sichtbare Slide ist deshalb nach dem Rebuild IMMER Index 0 -
    // der rohe data-index (Position unter ALLEN Slides) passt hier nicht.
    productInstance.settings.currentSlideIndex = 0;

    // WICHTIG: initProductSlider(variant) ueberschreibt currentSlideIndex selbst
    // wieder auf den data-index der statischen .starting-slide, sobald es OHNE
    // Argument aufgerufen wird (siehe theme.js initProductSlider, "if (!variant)").
    // Ein truthy Dummy-Wert ueberspringt diesen Zweig, damit unser Index 0 erhalten bleibt.
    productInstance.initProductSlider(true);

    // Erzwungene Anzeige: Wir verlassen uns nicht mehr auf die interne
    // Index-Berechnung des Themes, sondern befehlen Flickity direkt, das
    // erste (durch cellSelector ':not(.hide)' garantiert passende) Bild zu zeigen.
    requestAnimationFrame(() => {
      const flkty = productInstance.flickity && productInstance.flickity.slideshow;
      if (flkty && typeof flkty.select === 'function') {
        flkty.select(0, false, true);
      }

      // Höhe wieder freigeben, Wipe-Transition wieder erlauben
      slideshowContainer.style.minHeight = '';
      slideshowContainer.classList.remove('model-selector__no-wipe');

      // WICHTIG: Flickity verschiebt beim destroy/rebuild seine Zellen
      // (.product-main-slide) intern im DOM. Über mehrere Modellwechsel
      // hinweg driftet ihre Reihenfolge dadurch von der (nie angefassten)
      // Thumbnail-Reihenfolge auseinander. Das Theme markiert das aktive
      // Thumbnail aber per Positions-Index (childNavLinks[0]) - der zeigt
      // dann auf ein anderes Bild als tatsächlich im Hauptslider zu sehen
      // ist. Wir korrigieren das hier explizit über den Dateinamen statt
      // über die (unzuverlässige) Position.
      this.syncActiveThumbnail(slideshowContainer);
      this.repairThumbIndexMapping();
      this.repairSlideIndexMapping();

      // Wenn der jetzt aktive Slide ein Video enthaelt, dieses starten.
      // Flickity feuert bei select(0, ...) kein "change"-Event, wenn der
      // Index sich technisch nicht aendert - genau daran haengt aber die
      // Theme-eigene Video-Wiedergabe (siehe theme.js "onSlideChange" ->
      // "prepMediaOnSlide" -> "playMp4Video"). Ohne diesen Aufruf bleibt das
      // Video auf dem Standbild (Poster) stehen, obwohl der Slide korrekt
      // als aktiv markiert ist.
      this.ensureMediaPlaysOnSelectedSlide(productInstance, slideshowContainer);

      // Sobald das neue Bild geladen ist, das alte Overlay wegblenden -> Crossfade.
      const newImg = slideshowContainer.querySelector('.product-main-slide.is-selected img')
        || slideshowContainer.querySelector('.product-main-slide:not(.hide) img');
      this.fadeOutSliderOverlay(overlay, newImg);
    });
  }

  // Startet Video/3D-Media auf dem aktuell aktiven Slide, indem die
  // Theme-eigene Methode "prepMediaOnSlide" direkt aufgerufen wird - dieselbe
  // Methode, die das Theme normalerweise bei einem echten Flickity-
  // "change"-Event verwendet. So bleibt die Logik (mp4 abspielen, YouTube/
  // Vimeo-Player starten, 3D-Model-Viewer aktivieren) exakt konsistent mit
  // dem Rest des Themes, statt sie hier zu duplizieren.
  ensureMediaPlaysOnSelectedSlide(productInstance, slideshowContainer) {
    if (!productInstance || typeof productInstance.prepMediaOnSlide !== 'function') return;
    const selected = slideshowContainer.querySelector('.product-main-slide.is-selected');
    if (selected) productInstance.prepMediaOnSlide(selected);
  }

  // Setzt "is-active" auf genau das Thumbnail, dessen Bild-Dateiname mit dem
  // aktuell angezeigten Hauptbild uebereinstimmt - unabhaengig von Position
  // oder DOM-Reihenfolge.
  syncActiveThumbnail(slideshowContainer) {
    const selectedImg = slideshowContainer.querySelector('.product-main-slide.is-selected img');
    if (!selectedImg) return;
    const selectedFile = this.extractFileKey(selectedImg.currentSrc || selectedImg.src);
    if (!selectedFile) return;

    const thumbLinks = document.querySelectorAll('[data-product-thumbs] .product__thumb-item:not(.hide) [data-product-thumb]');
    let matched = null;
    thumbLinks.forEach(link => {
      const img = link.querySelector('img');
      const file = img ? this.extractFileKey(img.currentSrc || img.src) : '';
      const isMatch = file === selectedFile;
      link.classList.toggle('is-active', isMatch);
      if (isMatch) matched = link;
    });

    if (!matched) return;
    const parentItem = matched.closest('.product__thumb-item');
    if (parentItem && parentItem.parentElement) {
      const scroller = parentItem.parentElement;
      const isVertical = scroller.closest('[data-position="beside"]');
      if (isVertical) scroller.scrollTop = parentItem.offsetTop - 100;
      else scroller.scrollLeft = parentItem.offsetLeft - 100;
    }
  }

  extractFileKey(src) {
    if (!src) return '';
    return src.split('?')[0].split('/').pop().toLowerCase();
  }

  // Legt eine Kopie des zuletzt gezeigten Hauptbilds als Overlay über den
  // gesamten Slideshow-Container, damit der dahinterliegende Rebuild
  // (Höhenänderung, Bildwechsel) unsichtbar bleibt und stattdessen ein
  // sauberer Opacity-Crossfade zu sehen ist.
  createSliderFadeOverlay(slideshowContainer, currentImg) {
    // Falls durch schnell aufeinanderfolgende Modellwechsel noch ein
    // vorheriges Overlay aktiv ist, sofort entfernen statt es parallel
    // weiterlaufen zu lassen - sonst ueberlagern sich mehrere ein-/
    // ausblendende Bilder und es sieht aus wie ein Hin-und-her-Flackern.
    if (this._activeSliderOverlay && this._activeSliderOverlay.parentElement) {
      this._activeSliderOverlay.remove();
    }
    this._activeSliderOverlay = null;

    if (!currentImg || !currentImg.src) return null;

    const position = window.getComputedStyle(slideshowContainer).position;
    if (position === 'static') slideshowContainer.style.position = 'relative';

    const overlay = document.createElement('img');
    overlay.src = currentImg.currentSrc || currentImg.src;
    overlay.className = 'model-selector__slider-fade-overlay';
    Object.assign(overlay.style, {
      position: 'absolute', top: '0', left: '0', width: '100%', height: '100%',
      objectFit: 'cover', zIndex: '5', pointerEvents: 'none', opacity: '1',
      transition: 'opacity 0.4s ease-out'
    });
    slideshowContainer.appendChild(overlay);
    this._activeSliderOverlay = overlay;
    return overlay;
  }

  fadeOutSliderOverlay(overlay, newImg) {
    if (!overlay) return;
    const remove = () => {
      overlay.style.opacity = '0';
      setTimeout(() => {
        if (overlay.parentElement) overlay.remove();
        if (this._activeSliderOverlay === overlay) this._activeSliderOverlay = null;
      }, 420);
    };

    if (newImg && !(newImg.complete && newImg.naturalWidth > 0)) {
      newImg.addEventListener('load', remove, { once: true });
      setTimeout(remove, 600); // Sicherheitsnetz falls "load" nie feuert
    } else {
      remove();
    }
  }
  
  updateCollectionGrid(modelSlug) {
    const gridItems = document.querySelectorAll('.grid-product[data-product-handle]');
    gridItems.forEach(item => {
      const productHandle = item.dataset.productHandle;
      if (productHandle) this.updateGridItemImage(item, modelSlug);
    });
  }
  
  updateGridItemImage(gridItem, modelSlug) {
    const productId = gridItem.dataset.productId;
    if (!productId) return;
    const img = gridItem.querySelector('.grid-product__main-image');
    if (!img) return;
    const transitionKey = `${productId}-${modelSlug}`;
    if (this.pendingTransitions.has(transitionKey)) return;
    
    fetch(`/products/${gridItem.dataset.productHandle}.js`)
      .then(res => res.json())
      .then(product => {
        const matchingVariant = product.variants.find(v => {
          const option2 = v.option2;
          return option2 && this.normalizeModelName(option2) === modelSlug;
        });
        
        if (matchingVariant && matchingVariant.featured_image) {
          const rawImageSrc = matchingVariant.featured_image.src;
          const newImageSrc = this.getShopifyImageUrl(rawImageSrc, 540);
          if (img.src === newImageSrc) return;
          this.pendingTransitions.add(transitionKey);

          this.preloadImage(newImageSrc).then(preloadedImg => {
            const imageWrapper = img.parentElement;
            const existingOverlay = imageWrapper.querySelector('.grid-product__transition-overlay');
            if (existingOverlay) existingOverlay.remove();
            
            const overlayImg = document.createElement('img');
            overlayImg.className = img.className + ' grid-product__transition-overlay';
            overlayImg.src = newImageSrc;
            overlayImg.srcset = this.generateSrcset(newImageSrc);
            overlayImg.alt = img.alt;
            
            Object.assign(overlayImg.style, {
              position: 'absolute', top: '0', left: '0', width: '100%', height: '100%',
              opacity: '0', zIndex: '2', pointerEvents: 'none', willChange: 'opacity'
            });
            
            const wrapperPosition = window.getComputedStyle(imageWrapper).position;
            if (wrapperPosition === 'static') imageWrapper.style.position = 'relative';
            
            imageWrapper.appendChild(overlayImg);
            
            if (overlayImg.complete) this.startCrossFade(overlayImg, img, newImageSrc, transitionKey);
            else {
              overlayImg.onload = () => this.startCrossFade(overlayImg, img, newImageSrc, transitionKey);
              setTimeout(() => { if (overlayImg.parentElement) this.startCrossFade(overlayImg, img, newImageSrc, transitionKey); }, 1000);
            }
          }).catch(err => {
            img.src = newImageSrc; img.srcset = this.generateSrcset(newImageSrc);
            this.pendingTransitions.delete(transitionKey);
          });
        }
      }).catch(err => this.pendingTransitions.delete(transitionKey));
  }
  
  startCrossFade(overlayImg, originalImg, newImageSrc, transitionKey) {
    overlayImg.offsetHeight;
    requestAnimationFrame(() => { overlayImg.style.transition = 'opacity 0.4s ease-out'; overlayImg.style.opacity = '1'; });
    setTimeout(() => {
      originalImg.src = newImageSrc; originalImg.srcset = this.generateSrcset(newImageSrc);
      if (overlayImg.parentElement) overlayImg.remove();
      this.pendingTransitions.delete(transitionKey);
    }, 400);
  }
  
  preloadImage(src) {
    if (this.imageCache.has(src)) return Promise.resolve(this.imageCache.get(src));
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => { this.imageCache.set(src, img); resolve(img); };
      img.onerror = reject; img.src = src;
    });
  }
  
  updateVariantPicker() {
    this._updatingVariantPicker = true;
    const brandContainer = document.querySelector('[data-index="option1"]');
    const modelContainer = document.querySelector('[data-index="option2"]');
    if (!brandContainer || !modelContainer) { this._updatingVariantPicker = false; return; }
    
    const brandSelect = brandContainer.querySelector('select');
    if (brandSelect) {
      if (this.hasUserInteracted) {
        if (brandSelect.value !== this.selectedBrand) {
          brandSelect.value = this.selectedBrand;
          brandSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else brandSelect.value = '';
    } else {
      const brandInputs = brandContainer.querySelectorAll('input[data-variant-input]');
      brandInputs.forEach(input => {
        if (this.hasUserInteracted && input.value === this.selectedBrand) {
          if (!input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
        } else if (!this.hasUserInteracted) input.checked = false;
      });
    }
    
    setTimeout(() => {
      const modelSelect = modelContainer.querySelector('select');
      if (modelSelect) {
        if (this.hasUserInteracted) {
          if (modelSelect.disabled) modelSelect.disabled = false;
          const modelOption = Array.from(modelSelect.options).find(o => o.value === this.selectedModel);
          if (modelOption && modelSelect.value !== this.selectedModel) {
            modelSelect.value = this.selectedModel;
            modelSelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else { modelSelect.value = ''; modelSelect.disabled = true; }
      } else {
        const modelInputs = modelContainer.querySelectorAll('input[data-variant-input]');
        modelInputs.forEach(input => {
          if (this.hasUserInteracted && input.value === this.selectedModel) {
            if (!input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
          } else if (!this.hasUserInteracted) { input.checked = false; input.disabled = true; }
        });
      }
      if (this.hasUserInteracted) {
        this.triggerShopifyVariantUpdate();
        // Das Theme hat auf die obigen change-Events hin moeglicherweise
        // ueber seine eigene OptionSelectors-Logik (theme.js "_onSelectChange"
        // -> "variantImageChange" -> "updateVariantImage") den Hauptslider
        // per animiertem goToSlide() auf ein anderes Bild bewegt. Das
        // passiert komplett unabhaengig von unserer eigenen Filter-/Crossfade-
        // Logik und war die Ursache des Hin-und-her-Zitterns zwischen zwei
        // Bildern. Wir setzen deshalb unmittelbar danach (noch im selben,
        // synchronen Durchlauf) unser eigentlich gewaehltes Bild wieder
        // verbindlich durch.
        this.reassertActiveSlide();
      }
      this._updatingVariantPicker = false;
    }, 200);
  }

  triggerShopifyVariantUpdate() {
    // Beide Selektoren zeigen im Normalfall auf dasselbe Element (das
    // versteckte Shopify-Master-Select). Vorher wurde hier zweimal
    // hintereinander ein change-Event auf demselben Element ausgeloest,
    // was die Theme-eigene Variant-Logik unnoetig doppelt anstiess.
    const shopifySelect = document.querySelector('form[action*="/cart/add"] select[name="id"]')
      || document.querySelector('[name="id"]');
    if (shopifySelect) shopifySelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Erzwingt erneut das von uns gewaehlte Bild (Index 0 der gefilterten,
  // umsortierten Slides) als aktives Slide - als Korrektur, falls die
  // Theme-eigene Variant-Logik zwischenzeitlich woanders hinnavigiert hat.
  // Passiert z.B., wenn die Seite direkt mit "?variant=..." in der URL
  // geladen wird: Shopify waehlt dann serverseitig eine konkrete Variante
  // vor, und die eigene OptionSelectors-Logik des Themes kann den Slider
  // dabei unabhaengig von unserer Filterung anfassen.
  reassertActiveSlide() {
    if (!(window.theme && window.theme.sections && window.theme.sections.instances)) return;
    const productInstance = window.theme.sections.instances.find(inst => inst.type === 'product');
    const flkty = productInstance && productInstance.flickity && productInstance.flickity.slideshow;
    if (flkty && typeof flkty.select === 'function' && flkty.selectedIndex !== 0) {
      flkty.select(0, false, true);
    }
    const slideshow = document.querySelector('.product-slideshow');
    if (slideshow) {
      this.syncActiveThumbnail(slideshow);
      this.ensureMediaPlaysOnSelectedSlide(productInstance, slideshow);
    }
    this.repairThumbIndexMapping();
    this.repairSlideIndexMapping();
  }

  // Setzt die "data-index"-Attribute aller sichtbaren Hauptbild-Slides neu -
  // exakt nach ihrer Position unter den durch ":not(.hide)" gefilterten
  // Slides (0, 1, 2, ...), also genau der Index-Zaehlung, die Flickity
  // intern verwendet (cellSelector: ".product-main-slide:not(.hide)").
  // WICHTIG: Das Theme sucht in "onSlideChange" (theme.js) den naechsten
  // Slide per querySelector('.product-main-slide[data-index="'+index+'"]')
  // - also ueber dieses Attribut, NICHT ueber Flickitys eigene Zellliste.
  // Die Attribute stammen aber urspruenglich unveraendert aus Liquid (Position
  // unter ALLEN Bildern) und werden nirgendwo sonst aktualisiert. Sobald
  // Slides ausgeblendet sind, driften beide Zaehlungen auseinander - das
  // Theme trifft dann beim Video- oder 3D-Media-Start (und beim Setzen von
  // tabindex/tastatur-Fokus) auf ein komplett falsches (verstecktes)
  // Element, weshalb z.B. Videos nie tatsaechlich abgespielt werden.
  //
  // WICHTIG: Ausgeblendete Slides muessen dabei auf Werte AUSSERHALB des
  // 0..N-1-Bereichs verschoben werden. Liessen wir ihre alten (rohen)
  // data-index-Werte unangetastet, wuerden sie mit den neu vergebenen
  // Werten der sichtbaren Slides kollidieren (z.B. ein verstecktes Bild mit
  // urspruenglich data-index="5" UND unser neu vergebenes data-index="5"
  // beim sichtbaren Video-Slide) - querySelector('[data-index="5"]')
  // liefert dann das ERSTE Treffer-Element in DOM-Reihenfolge, was leicht
  // das falsche (versteckte) sein kann.
  repairSlideIndexMapping() {
    const allSlides = document.querySelectorAll('.product-main-slide');
    const visible = [];
    const hidden = [];
    allSlides.forEach(slide => {
      (slide.classList.contains('hide') ? hidden : visible).push(slide);
    });
    visible.forEach((slide, i) => slide.setAttribute('data-index', i));
    hidden.forEach((slide, i) => slide.setAttribute('data-index', visible.length + i));
  }

  // Setzt die "data-index"-Attribute aller sichtbaren Thumbnail-Links neu -
  // exakt nach demselben Schema, das Flickitys eigenes initChildNav()
  // beim (Neu-)Aufbau verwendet (Position unter den sichtbaren Thumbnails,
  // von 0 an durchgezaehlt). Diese Attribute bestimmen, zu welchem Slide ein
  // Klick auf ein Thumbnail navigiert (siehe theme.js "getChildIndex").
  // Sie werden aber nur beim tatsaechlichen Flickity-(Neu-)Aufbau aktuell
  // gehalten - hat zwischenzeitlich ein anderer Prozess (z.B. die durch
  // "?variant=..." ausgeloeste Theme-eigene Variantenlogik) den Slider ohne
  // unser Wissen neu aufgebaut oder navigiert, bleiben sie auf einem alten
  // Stand haengen. Weil wir Hauptbilder UND Thumbnails mit derselben
  // Prioritaets-Logik (computeModelPriority) sortieren, entspricht die
  // sichtbare Thumbnail-Reihenfolge hier immer exakt der sichtbaren
  // Slide-Reihenfolge - die Neuvergabe ist deshalb unabhaengig vom
  // tatsaechlichen internen Flickity-Zustand garantiert korrekt.
  repairThumbIndexMapping() {
    const visibleThumbLinks = document.querySelectorAll('[data-product-thumbs] .product__thumb-item:not(.hide) [data-product-thumb]');
    visibleThumbLinks.forEach((link, i) => {
      link.setAttribute('data-index', i);
    });
  }
  
  syncWithVariantPicker(variant) {
    if (this._updatingVariantPicker) return;
    if (variant.option1 && variant.option2) {
      // Ausnahme: option1 muss eine bekannte Handymarke sein (Apple, Samsung, Google, Xiaomi).
      // Sonst kommt das Event von einem anderen Produkt (z.B. Acrylblock mit Größe/Orientierung)
      // und soll den Model-Selector nicht überschreiben.
      if (!this.data || !this.data.models || !Object.prototype.hasOwnProperty.call(this.data.models, variant.option1)) {
        return;
      }
      this.hasUserInteracted = true;
      if (this.selectedBrand !== variant.option1 || this.selectedModel !== variant.option2) {
        this._syncingFromVariantPicker = true;
        this.selectedBrand = variant.option1;
        this.selectedModel = variant.option2;
        this.setCookie(this.data.cookieName, JSON.stringify({ brand: this.selectedBrand, model: this.selectedModel }), this.data.cookieExpiry);
        this.updateUI();
        this.applyModelToPage();

        document.dispatchEvent(new CustomEvent('model:selected', { detail: { brand: this.selectedBrand, model: this.selectedModel } }));
        this._syncingFromVariantPicker = false;
      }
    }
  }

  normalizeModelName(model) { return model.replace(/\s+/g, '-').replace(/\+/g, '-Plus').normalize('NFD').replace(/[\u0300-\u036f]/g, ''); }
  getShopifyImageUrl(src, width) { if (!src) return ''; let c = src.replace(/&width=\d+/g, ''); return `${c}${c.includes('?') ? '&' : '?'}width=${width}`; }
  generateSrcset(src) { const widths = [160, 240, 360, 420, 540]; return widths.map(w => `${this.getShopifyImageUrl(src, w)} ${w}w`).join(', '); }
  debounce(func, wait) { let timeout; return function (...args) { clearTimeout(timeout); timeout = setTimeout(() => func.apply(this, args), wait); }; }
  setCookie(n, v, d) { const e = new Date(); e.setTime(e.getTime() + d * 24 * 60 * 60 * 1000); document.cookie = `${n}=${v};expires=${e.toUTCString()};path=/;SameSite=Lax`; }
  getCookie(n) { const nameEQ = n + '='; const ca = document.cookie.split(';'); for (let i = 0; i < ca.length; i++) { let c = ca[i]; while (c.charAt(0) === ' ') c = c.substring(1, c.length); if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length); } return null; }
  deleteCookie(n) { document.cookie = `${n}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`; }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => window.modelSelectorInstance = new ModelSelector());
else window.modelSelectorInstance = new ModelSelector();
window.CasartoModelSelector = ModelSelector;